/**
 * Skylab Explorer API contracts (U4).
 *
 * The Explorer is a generic SKY/Legacy request browser. It registers four API
 * surfaces and forwards calls through one unified backend proxy
 * (`POST /internal/skylab/proxy`). Three of the four are per-user OAuth APIs
 * (every call is issued AS a specific OAuth'd user — there is no app-level /
 * school-wide SKY token); the fourth (Legacy) uses a single school-wide bearer.
 *
 * Type names are prefixed `Skylab*` — the root shared-types barrel re-exports
 * every `api/*` module with `export *`, so the prefix avoids collisions.
 */

import { z } from 'zod'

/** The four registered Explorer API surfaces. */
export const SkylabApiKeySchema = z.enum(['sky-school-core', 'sky-enrollment-mgmt', 'sky-oneroster', 'legacy'])
export type SkylabApiKey = z.infer<typeof SkylabApiKeySchema>

/**
 * Auth model for an API surface:
 * - `oauth-user`    — call is issued as a specific OAuth'd user (Acting-as combo)
 * - `legacy-bearer` — call uses the single school-wide legacy bearer token
 */
export const SkylabAuthModelSchema = z.enum(['oauth-user', 'legacy-bearer'])
export type SkylabAuthModel = z.infer<typeof SkylabAuthModelSchema>

/**
 * GET and POST only (SCH-382): the proxy forwards a POST to SKY's list queries
 * and the attendance writes, and refuses every other write.
 */
export const SkylabHttpMethodSchema = z.enum(['GET', 'POST'])
export type SkylabHttpMethod = z.infer<typeof SkylabHttpMethodSchema>

/**
 * Documentation schema for an API-registry entry. The authoritative registry
 * lives web-side (`explorer-shell/api-registry.ts`); this schema pins the
 * shape both sides agree on so a future server-driven registry can reuse it.
 */
export const SkylabApiRegistryEntrySchema = z.object({
  key: SkylabApiKeySchema,
  /** Display name, e.g. "SKY · School Core". */
  label: z.string(),
  authModel: SkylabAuthModelSchema,
  /** Upstream host the proxy forwards to (no trailing slash). */
  baseUrl: z.string(),
  /** Domain identifiers registered under this API (e.g. "people", "sections"). */
  domains: z.array(z.string())
})
export type SkylabApiRegistryEntry = z.infer<typeof SkylabApiRegistryEntrySchema>

/**
 * Proxy request. `path` is the full upstream path AFTER the API's base host
 * (e.g. `/school/v1/users`); the backend prepends the registered base URL.
 *
 * Auth selector is mutually exclusive by API:
 * - OAuth APIs require `actingAsUserId` (UsersOnSchools.publicId).
 * - Legacy requires `useLegacyBearer: true`.
 * The backend enforces the pairing; bad combinations are rejected with 400.
 */
export const SkylabProxyRequestSchema = z.object({
  /** School public ID (active school). */
  schoolId: z.string().min(1),
  api: SkylabApiKeySchema,
  method: SkylabHttpMethodSchema,
  path: z.string().min(1),
  /** Query params as a flat string map. */
  query: z.record(z.string(), z.string()).optional(),
  /** Request body for write methods. */
  body: z.unknown().optional(),
  /** Required for OAuth APIs — the user the call is issued as. */
  actingAsUserId: z.string().min(1).optional(),
  /** Required for the Legacy API. */
  useLegacyBearer: z.boolean().optional()
})
export type SkylabProxyRequest = z.infer<typeof SkylabProxyRequestSchema>

/**
 * Echo of the assembled upstream request. Secret headers are masked
 * (last-4 only) — the proxy never returns live tokens to the browser.
 */
export const SkylabProxyRequestMetaSchema = z.object({
  method: SkylabHttpMethodSchema,
  url: z.string(),
  query: z.record(z.string(), z.string()),
  /** Header map with secret values masked. */
  headers: z.record(z.string(), z.string()),
  body: z.unknown().nullable(),
  /** The OAuth'd user the call was issued as, or null for Legacy. */
  issuedAs: z
    .object({
      id: z.string(),
      name: z.string(),
      isAdmin: z.boolean()
    })
    .nullable()
})
export type SkylabProxyRequestMeta = z.infer<typeof SkylabProxyRequestMetaSchema>

export const SkylabProxyResponseSchema = z.object({
  /** Upstream HTTP status (0 when the call never left — config/network error). */
  status: z.number().int(),
  /** True for 2xx upstream responses. */
  ok: z.boolean(),
  durationMs: z.number().int().nonnegative(),
  request: SkylabProxyRequestMetaSchema,
  /** Parsed upstream body (JSON when possible, else raw string). */
  response: z.unknown(),
  /** Human-readable error when the proxy could not complete the call. */
  error: z.string().nullable()
})
export type SkylabProxyResponse = z.infer<typeof SkylabProxyResponseSchema>
